// contexts/LogSessionContext.tsx
import { createContext, useContext, useState } from "react";
import { useUserData } from "./UserContext";
import {
  DayRecord,
  MealRecord,
  FishConsumptionUnit,
  PersonRecord,
  User,
  MealRecordSession,
} from "../types/data";
import { logService } from "../services/log.service";

interface LogSessionContextValue {
  mode: "user" | "survey";
  person: PersonRecord;
  user: User;
  currentDayRecord: DayRecord | null;
  setcurrentDayRecord: (record: DayRecord | null) => void;
  currentMealRecord: MealRecord | null;
  setcurrentMealRecord: (record: MealRecord | null) => void;
  newFishItem: FishConsumptionUnit | null;
  setNewFishItem: (item: FishConsumptionUnit | null) => void;
  commitFishItemToMeal: () => void;
  trackFishItemInSession: (fishItemId: string) => void;
  commitMealToDay: (overwrite?: boolean, mealToCommit?: MealRecord) => Promise<void>;
  exitFallback?: string;
  setExitFallback?: (path: string) => void;
}

const LogSessionContext = createContext<LogSessionContextValue | null>(null);

export function useLogSession() {
  const ctx = useContext(LogSessionContext);
  if (!ctx) throw new Error("LogSessionContext not available");
  return ctx;
}

export function LogSessionProvider({
  mode,
  person,
  user,
  children,
}: {
  mode: "user" | "survey";
  person: PersonRecord;
  user: User;
  initialDate?: string;
  children: React.ReactNode;
}) {
  const { updatePerson } = useUserData();

  const [currentDayRecord, setcurrentDayRecordState] =
    useState<DayRecord | null>(() => {
      const stored = sessionStorage.getItem("logSession.currentDayRecord");
      return stored ? JSON.parse(stored) : null;
    });

  const [currentMealRecord, setcurrentMealRecordState] =
    useState<MealRecord | null>(() => {
      const stored = sessionStorage.getItem("logSession.currentMealRecord");
      return stored ? JSON.parse(stored) : null;
    });

  const [newFishItem, setNewFishItemState] =
    useState<FishConsumptionUnit | null>(() => {
      const stored = sessionStorage.getItem("logSession.newFishItem");
      return stored ? JSON.parse(stored) : null;
    });

  const [exitFallback, setExitFallback] = useState<string>(
    mode === "survey" ? "/survey/person-list" : "/user"
  );

  // Track fish items added in current session
  const [currentSessionFishIds, setCurrentSessionFishIds] = useState<string[]>(
    () => {
      const stored = sessionStorage.getItem("logSession.currentSessionFishIds");
      return stored ? JSON.parse(stored) : [];
    }
  );

  const setcurrentDayRecord = (record: DayRecord | null) => {
    // console.log("Setting current day record:", record);
    setcurrentDayRecordState(record);
    if (record) {
      sessionStorage.setItem(
        "logSession.currentDayRecord",
        JSON.stringify(record)
      );
    } else {
      sessionStorage.removeItem("logSession.currentDayRecord");
    }
  };

  const setcurrentMealRecord = (record: MealRecord | null) => {
    // console.log("Setting current meal record:", record);
    
    if (record) {
      // Only reset session tracking when starting a NEW meal session
      // Check if this is a completely new meal (no ID match with current) or first meal
      const isNewMealSession = !currentMealRecord || currentMealRecord.id !== record.id;
      
      if (isNewMealSession) {
        // Reset session fish IDs when starting a new meal editing session
        console.log('[LogSession] Starting NEW meal session, resetting fish IDs');
        setCurrentSessionFishIds([]);
        sessionStorage.setItem("logSession.currentSessionFishIds", JSON.stringify([]));
        
        // Store session start time
        sessionStorage.setItem("logSession.mealSessionStart", new Date().toISOString());
      } else {
        // Same meal being updated - preserve session tracking
        console.log('[LogSession] Updating SAME meal, preserving session fish IDs');
      }
    } else {
      // Clear session tracking when clearing meal record
      console.log('[LogSession] Clearing meal record and session tracking');
      setCurrentSessionFishIds([]);
      sessionStorage.removeItem("logSession.currentSessionFishIds");
      sessionStorage.removeItem("logSession.mealSessionStart");
    }
    
    setcurrentMealRecordState(record);
    if (record) {
      sessionStorage.setItem(
        "logSession.currentMealRecord",
        JSON.stringify(record)
      );
    } else {
      sessionStorage.removeItem("logSession.currentMealRecord");
    }
  };

  const setNewFishItem = (item: FishConsumptionUnit | null) => {
    setNewFishItemState(item);
    if (item) {
      sessionStorage.setItem("logSession.newFishItem", JSON.stringify(item));
    } else {
      sessionStorage.removeItem("logSession.newFishItem");
    }
  };

  const commitFishItemToMeal = () => {
    if (!currentMealRecord || !newFishItem) return;
    
    // Track this fish item in current session
    trackFishItemInSession(newFishItem.id);
    
    const updatedMeal: MealRecord = {
      ...currentMealRecord,
      fish_items: [...currentMealRecord.fish_items, newFishItem],
      updated_at: new Date().toISOString(),
    };
    setcurrentMealRecord(updatedMeal);
    setNewFishItem(null);
  };

  const trackFishItemInSession = (fishItemId: string) => {
    // Add fish item to session tracking (avoid duplicates)
    if (!currentSessionFishIds.includes(fishItemId)) {
      const updatedSessionFishIds = [...currentSessionFishIds, fishItemId];
      console.log('[LogSession] Tracking fish item in session:', fishItemId, 'Total tracked:', updatedSessionFishIds.length);
      setCurrentSessionFishIds(updatedSessionFishIds);
      sessionStorage.setItem(
        "logSession.currentSessionFishIds",
        JSON.stringify(updatedSessionFishIds)
      );
    } else {
      console.log('[LogSession] Fish item already tracked:', fishItemId);
    }
  };

  const mergeFishItems = (
    oldItems: FishConsumptionUnit[],
    newItems: FishConsumptionUnit[]
  ): FishConsumptionUnit[] => {
    const mergedItems = [...oldItems];
    newItems.forEach((newItem) => {
      const existingIndex = mergedItems.findIndex(
        (item) => item.id === newItem.id
      );
      if (existingIndex !== -1) {
        // Update existing item
        mergedItems[existingIndex] = newItem;
      } else {
        // Add new item
        mergedItems.push(newItem);
      }
    });
    return mergedItems;
  };

  const mergeMealRecords = (
    oldRecords: MealRecord[],
    newRecords: MealRecord[]
  ): MealRecord[] => {
    let mergedRecords = [...oldRecords];
    newRecords.forEach((newRecord) => {
      let existingIndex = mergedRecords.findIndex(
        (record) => record.id === newRecord.id
      );
      if (existingIndex !== -1) {
        // Update existing record
        mergedRecords[existingIndex].fish_items = mergeFishItems(
          mergedRecords[existingIndex].fish_items || [],
          newRecord.fish_items || []
        );
        mergedRecords[existingIndex].updated_at =
          newRecord.updated_at || new Date().toISOString();
      } else {
        // Add new record
        mergedRecords.push(newRecord);
      }
    });
    return mergedRecords;
  };

  const mergeDayRecords = (
    dayRecords: DayRecord[],
    newRecord: DayRecord
  ): DayRecord[] => {
    const existingRecordIndex = dayRecords.findIndex(
      (record) => record.date === newRecord.date
    );
    if (existingRecordIndex !== -1) {
      // Merge with existing record
      const existingRecord = dayRecords[existingRecordIndex];
      const mergedRecord: DayRecord = {
        ...existingRecord,
        meals: mergeMealRecords(
          existingRecord.meals || [],
          newRecord.meals || []
        ),
      };
      return [
        ...dayRecords.slice(0, existingRecordIndex),
        mergedRecord,
        ...dayRecords.slice(existingRecordIndex + 1),
      ];
    } else {
      // Add new record
      return [...dayRecords, newRecord];
    }
  };

  const commitMealToDay = async (overwrite = false, mealToCommit?: MealRecord) => {
    const startTime = Date.now();
    
    try {
      // Use provided meal or fall back to state
      let mealRecord = mealToCommit || currentMealRecord;
      
      if (!currentDayRecord || !mealRecord) return;

    // Get the session start time
    // Priority: 1) sessionStorage, 2) existing incomplete session, 3) current time
    let sessionStartTime: string;
    const storedStart = sessionStorage.getItem('logSession.mealSessionStart');
    const existingMeal = currentDayRecord.meals.find(m => m.id === mealRecord!.id);
    
    // Determine session start time
    if (storedStart) {
      // Use stored start time (highest priority)
      sessionStartTime = storedStart;
      console.log('[LogSession] Using stored session start time:', sessionStartTime);
    } else if (existingMeal?.log_sessions && existingMeal.log_sessions.length > 0) {
      // For existing meal, check if last session is incomplete
      const lastSession = existingMeal.log_sessions[existingMeal.log_sessions.length - 1];
      if (!lastSession.completed_at) {
        // Continue incomplete session
        sessionStartTime = lastSession.started_at;
        console.log('[LogSession] Continuing incomplete session from:', sessionStartTime);
      } else {
        // Last session was complete, but no stored start time - use meal updated_at or current time
        sessionStartTime = mealRecord.updated_at || new Date().toISOString();
        console.log('[LogSession] No stored start time, using fallback:', sessionStartTime);
      }
    } else {
      // New meal without stored start time - use meal updated_at or current time
      sessionStartTime = mealRecord.updated_at || new Date().toISOString();
      console.log('[LogSession] New meal, using fallback start time:', sessionStartTime);
    }

    const sessionCompletedTime = new Date().toISOString();

    // Get fish IDs from sessionStorage as fallback in case state hasn't updated yet
    const storedFishIds = sessionStorage.getItem('logSession.currentSessionFishIds');
    const sessionFishIds = storedFishIds ? JSON.parse(storedFishIds) : currentSessionFishIds;
    
    // Log current session state before creating session record
    console.log('[LogSession] Current session state:', {
      currentSessionFishIds: currentSessionFishIds,
      sessionStorageFishIds: storedFishIds,
      actuallyUsing: sessionFishIds,
    });

    // Build the session record with fish items added/modified in this session
    const newSession: MealRecordSession = {
      started_at: sessionStartTime,
      completed_at: sessionCompletedTime,
      fish_item_ids: sessionFishIds,
    };

    console.log('[LogSession] Created session record:', newSession);

    // Add session to meal record - store in const to preserve type checking
    const updatedMealRecord: MealRecord = {
      ...mealRecord,
      log_sessions: [...(mealRecord.log_sessions || []), newSession],
    };

    const sessionDurationMs = new Date(sessionCompletedTime).getTime() - new Date(sessionStartTime).getTime();
    
    console.log('[LogSession] Session details:', {
      sessionStartTime,
      sessionCompletedTime,
      durationSeconds: (sessionDurationMs / 1000).toFixed(1),
      fishItemsTracked: currentSessionFishIds.length,
      fishItemIds: currentSessionFishIds,
    });
    
    logService.info('Log Session', 'Committing meal to day - START', {
      overwrite,
      date: currentDayRecord.date,
      existingMealCount: currentDayRecord.meals?.length || 0,
      currentMealId: updatedMealRecord.id,
      fishItemCount: updatedMealRecord.fish_items?.length || 0,
      sessionFishItemCount: sessionFishIds.length,
      sessionDurationSeconds: (sessionDurationMs / 1000).toFixed(1),
      totalSessions: updatedMealRecord.log_sessions?.length || 0,
    });

    let updatedMeals: MealRecord[] = [];

    if (overwrite) {
      // Overwrite the existing meal record, if new record is empty, remove the meal
      if (updatedMealRecord.fish_items.length === 0) {
        updatedMeals = currentDayRecord.meals.filter(
          (meal) => meal.id !== updatedMealRecord.id
        );
      } else {
        if (
          currentDayRecord.meals.some(
            (meal) => meal.id === updatedMealRecord.id
          )
        ) {
          // Update the existing meal record
          updatedMeals = currentDayRecord.meals.map((meal) =>
            meal.id === updatedMealRecord.id ? updatedMealRecord : meal
          );
        } else {
          // Add the new meal record
          updatedMeals = [...(currentDayRecord.meals || []), updatedMealRecord];
        }
      }
    } else {
      // Add the new meal record
      updatedMeals = mergeMealRecords(currentDayRecord.meals || [], [
        updatedMealRecord,
      ]);
    }

    logService.info('Log Session', 'Updated meals for day', {
      date: currentDayRecord.date,
      mealCount: updatedMeals.length,
      totalFishItems: updatedMeals.reduce((sum, m) => sum + (m.fish_items?.length || 0), 0),
    });

    const updatedDay: DayRecord = {
      ...currentDayRecord,
      meals: updatedMeals.filter((meal) => meal.fish_items.length > 0), // Remove empty meals
    };
    setcurrentDayRecord(updatedDay);
    setcurrentMealRecord(null);

    if (overwrite) {
      let updatedRecords = null;
      if (updatedDay.meals.length > 0) {
        updatedRecords = person.records.map((record) =>
          record.date === currentDayRecord.date ? updatedDay : record
        );
        if (!updatedRecords.some((record) => record.date === updatedDay.date)) {
          updatedRecords.push(updatedDay);
        }
      } else {
        updatedRecords = person.records.filter(
          (record) => record.date !== currentDayRecord.date
        );
      }

      if (updatedRecords) {
        const updatedPerson: PersonRecord = {
          ...person,
          records: updatedRecords,
          updated_at: new Date().toISOString(),
        };
        logService.info('Log Session', 'Saving updated person (overwrite mode)', {
          personId: updatedPerson.id,
          recordCount: updatedRecords.length,
        });
        await updatePerson(updatedPerson);
        logService.info('Log Session', 'Person saved successfully (overwrite mode)', {
          duration: Date.now() - startTime,
        });
      }
    } else {
      // Update the person record with the new day record, or merge if a record for the day already exists
      const updatedPerson: PersonRecord = {
        ...person,
        records: mergeDayRecords(person.records, updatedDay),
        updated_at: new Date().toISOString(),
        synced_at: '1970-01-01T00:00:00.000Z', // Force sync when records change
      };
      logService.info('Log Session', 'Saving updated person (merge mode)', {
        personId: updatedPerson.id,
        recordCount: updatedPerson.records.length,
      });
      await updatePerson(updatedPerson);
      
      logService.info('Log Session', 'Person saved successfully (merge mode)', {
        duration: Date.now() - startTime,
      });
    }
    } catch (error) {
      logService.error('Log Session', 'commitMealToDay FAILED', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        duration: Date.now() - startTime,
      });
      throw error; // Re-throw to let caller handle
    }
  };

  return (
    <LogSessionContext.Provider
      value={{
        mode,
        person,
        user,
        currentDayRecord,
        setcurrentDayRecord,
        currentMealRecord,
        setcurrentMealRecord,
        newFishItem,
        setNewFishItem,
        commitFishItemToMeal,
        trackFishItemInSession,
        commitMealToDay,
        exitFallback,
        setExitFallback,
      }}
    >
      {children}
    </LogSessionContext.Provider>
  );
}
